import { css } from '@emotion/core';
import styled from '@emotion/styled';
import {
  Input as BaseInput,
  Box,
  Flex,
  Icon,
  NakedButton,
} from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';

import Text from '@/components/Text';
import FormControl from '@/v2/components/ui/FormControl';

export interface InputProps extends Omit<BaseInput.BoxProps, 'onClick'> {
  icon: string;
  label: string;
  placeholder: string;
  onClick: Function;
  value?: string;
  error?: string;
  disabled?: boolean;
  dataTestId?: string;
}

const InputContainer = styled(NakedButton)`
  text-align: left;

  font-size: ${themeGet('fontSizes.sm')};

  background-color: ${themeGet('components.input.backgroundColor')};

  border: ${themeGet('components.input.border')};
  border-radius: 3px;

  margin: 0;

  cursor: pointer;

  ${(props: InputProps) =>
    props.height &&
    css`
      height: ${props.height};
    `};

  ${(props: InputProps) => {
    return props.error
      ? css`
          border-color: ${themeGet('components.input.error.borderColor')(
            props,
          )};
        `
      : css`
          border-color: ${themeGet('components.input.borderColor')(props)};
        `;
  }}

  &:active,
  &:focus {
    outline: none;
    border-color: ${themeGet('components.input.focus.borderColor')};
  }

  &:disabled,
  &:disabled:hover {
    background-color: ${themeGet('components.input.disabled.backgroundColor')};
    border-color: ${themeGet('components.input.borderColor')};
  }
`;

const getColor = (
  name: string,
  { disabled, ...props }: { disabled: boolean },
) => css`
  color: ${themeGet(
    'components.input.' + name + (disabled ? '.disabled' : '') + '.color',
  )(props)};
`;

const Label = styled(Text)`
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.1px;

  ${(props: any) => getColor('label', props)}
`;

const Value = styled(Text)`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;

  ${(props: any) => getColor('text', props)}
`;

const Placeholder = styled(Text)`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;

  ${(props: any) => getColor('placeholder', props)}
`;

const StyledIcon = styled(Icon)`
  display: 'absolute';

  ${(props: any) => getColor('icon', props)}
`;

const Input = ({
  label,
  placeholder,
  icon,
  value,
  error = undefined,
  onClick,
  width,
  height,
  dataTestId,
  disabled = false,
}: InputProps) => {
  return (
    <FormControl
      flexDirection="column"
      position="relative"
      onClick={onClick}
      error={error}
      data-testid={`${dataTestId}`}
    >
      <InputContainer
        width={width}
        height={height}
        disabled={disabled}
        error={error}
        data-testid={`${dataTestId}_CONTAINER`}
      >
        <Flex
          position="absolute"
          top="12px"
          left="16px"
          flexDirection="column"
          gap="8px"
        >
          <Label disabled={disabled}>{label}</Label>
          {value ? (
            <Value disabled={disabled}>{value}</Value>
          ) : (
            <Placeholder disabled={disabled}>{placeholder}</Placeholder>
          )}
        </Flex>
        <Box
          width="48px"
          position="absolute"
          top={height ? `calc(${height}/2 - 12px)` : '12px'}
          right="0"
          alignItems="center"
          justifyContent="center"
        >
          <StyledIcon
            size={24}
            name={icon}
            disabled={disabled}
            data-testid={`${dataTestId}_ICON`}
          />
        </Box>
      </InputContainer>
    </FormControl>
  );
};

export default Input;
